/**
 * Session Recording Store
 *
 * Manages the lifecycle of in-session audio recordings and their transcript
 * segments. Each recording is scoped to a prioritization session and workspace.
 *
 * Responsibilities:
 * - Create / update / complete recording rows
 * - Append transcript segments as they arrive from the browser speech API
 * - Subscribe to Supabase Realtime so all participants see the same transcript
 * - Expose a clean interface to the SessionTranscriptTab component
 */

import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import {
  type SessionRecording,
  type TranscriptSegment,
  type SessionRecordingRow,
  type TranscriptSegmentRow,
  rowToRecording,
  rowToSegment,
} from '../../types/prioritization/sessionRecording';

// ─── State shape ──────────────────────────────────────────────────────────────

interface SessionRecordingStoreState {
  recordings: SessionRecording[];
  activeRecordingId: string | null;
  segments: TranscriptSegment[];
  isLoading: boolean;
  error: string | null;

  _channel: RealtimeChannel | null;

  // ── Actions ────────────────────────────────────────────────────────────────

  loadRecordings(sessionId: string): Promise<void>;
  loadSegments(recordingId: string): Promise<void>;

  startRecording(
    sessionId: string,
    workspaceId: string,
    userId: string,
  ): Promise<string | null>;

  pauseRecording(recordingId: string): Promise<void>;
  resumeRecording(recordingId: string): Promise<void>;
  completeRecording(recordingId: string, durationMs: number): Promise<void>;
  failRecording(recordingId: string): Promise<void>;

  appendSegment(
    segment: Omit<TranscriptSegment, 'id' | 'createdAt'>,
  ): Promise<void>;

  subscribeToSegments(sessionId: string): void;
  unsubscribe(): void;
  reset(): void;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useSessionRecordingStore = create<SessionRecordingStoreState>()(
  (set, get) => ({
    recordings: [],
    activeRecordingId: null,
    segments: [],
    isLoading: false,
    error: null,
    _channel: null,

    // ── Load recordings ──────────────────────────────────────────────────────

    async loadRecordings(sessionId) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('prioritization_session_recordings')
        .select('*')
        .eq('session_id', sessionId)
        .order('started_at', { ascending: false });

      if (error) {
        set({ isLoading: false, error: error.message });
        return;
      }

      const recordings = (data as SessionRecordingRow[]).map(rowToRecording);
      const active = recordings.find(
        (r) => r.status === 'recording' || r.status === 'paused',
      );
      set({
        recordings,
        activeRecordingId: active?.id ?? null,
        isLoading: false,
      });
    },

    // ── Load segments for a recording ────────────────────────────────────────

    async loadSegments(recordingId) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      const { data, error } = await supabase
        .from('prioritization_session_transcript_segments')
        .select('*')
        .eq('recording_id', recordingId)
        .eq('is_final', true)
        .order('start_ms', { ascending: true });

      if (error) return;

      set({ segments: (data as TranscriptSegmentRow[]).map(rowToSegment) });
    },

    // ── Start a new recording ────────────────────────────────────────────────

    async startRecording(sessionId, workspaceId, userId) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return null;

      const id = generateUUID();
      const { error } = await supabase
        .from('prioritization_session_recordings')
        .insert({
          id,
          session_id: sessionId,
          workspace_id: workspaceId,
          status: 'recording',
          created_by: userId,
        });

      if (error) {
        set({ error: error.message });
        return null;
      }

      const recording: SessionRecording = {
        id,
        sessionId,
        workspaceId,
        status: 'recording',
        startedAt: new Date().toISOString(),
        segmentCount: 0,
        createdBy: userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      set((s) => ({
        recordings: [recording, ...s.recordings],
        activeRecordingId: id,
        segments: [],
      }));

      return id;
    },

    // ── Pause ────────────────────────────────────────────────────────────────

    async pauseRecording(recordingId) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      await supabase
        .from('prioritization_session_recordings')
        .update({ status: 'paused' })
        .eq('id', recordingId);

      set((s) => ({
        recordings: s.recordings.map((r) =>
          r.id === recordingId ? { ...r, status: 'paused' } : r,
        ),
      }));
    },

    // ── Resume ───────────────────────────────────────────────────────────────

    async resumeRecording(recordingId) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      await supabase
        .from('prioritization_session_recordings')
        .update({ status: 'recording' })
        .eq('id', recordingId);

      set((s) => ({
        recordings: s.recordings.map((r) =>
          r.id === recordingId ? { ...r, status: 'recording' } : r,
        ),
      }));
    },

    // ── Complete ─────────────────────────────────────────────────────────────

    async completeRecording(recordingId, durationMs) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      const endedAt = new Date().toISOString();

      await supabase
        .from('prioritization_session_recordings')
        .update({ status: 'complete', ended_at: endedAt, duration_ms: durationMs })
        .eq('id', recordingId);

      set((s) => ({
        recordings: s.recordings.map((r) =>
          r.id === recordingId
            ? { ...r, status: 'complete', endedAt, durationMs }
            : r,
        ),
        activeRecordingId: null,
      }));
    },

    // ── Fail ─────────────────────────────────────────────────────────────────

    async failRecording(recordingId) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      await supabase
        .from('prioritization_session_recordings')
        .update({ status: 'failed' })
        .eq('id', recordingId);

      set((s) => ({
        recordings: s.recordings.map((r) =>
          r.id === recordingId ? { ...r, status: 'failed' } : r,
        ),
        activeRecordingId: s.activeRecordingId === recordingId
          ? null
          : s.activeRecordingId,
      }));
    },

    // ── Append a transcript segment ──────────────────────────────────────────

    async appendSegment(segment) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      const id = generateUUID();
      const now = new Date().toISOString();

      const row = {
        id,
        recording_id: segment.recordingId,
        session_id: segment.sessionId,
        workspace_id: segment.workspaceId,
        speaker_user_id: segment.speakerUserId ?? null,
        speaker_label: segment.speakerLabel,
        text: segment.text,
        start_ms: segment.startMs,
        end_ms: segment.endMs,
        is_final: segment.isFinal,
        confidence: segment.confidence ?? null,
      };

      const { error } = await supabase
        .from('prioritization_session_transcript_segments')
        .insert(row);

      if (error) return;

      const full: TranscriptSegment = {
        ...segment,
        id,
        createdAt: now,
      };

      if (segment.isFinal) {
        set((s) => ({ segments: [...s.segments, full] }));

        // Increment segment_count on the recording row
        await supabase.rpc('increment_recording_segment_count', {
          p_recording_id: segment.recordingId,
        }).catch(() => {
          // RPC may not exist yet — silently ignore
        });
      }
    },

    // ── Realtime subscription ─────────────────────────────────────────────────

    subscribeToSegments(sessionId) {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      const existing = get()._channel;
      if (existing) {
        void supabase.removeChannel(existing);
      }

      const channel = supabase
        .channel(`session-transcript:${sessionId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'prioritization_session_transcript_segments',
            filter: `session_id=eq.${sessionId}`,
          },
          (payload) => {
            const row = payload.new as TranscriptSegmentRow;
            if (!row.is_final) return;
            const segment = rowToSegment(row);
            set((s) => {
              // Deduplicate — own writes land here too
              if (s.segments.some((seg) => seg.id === segment.id)) return s;
              return { segments: [...s.segments, segment] };
            });
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'prioritization_session_recordings',
            filter: `session_id=eq.${sessionId}`,
          },
          (payload) => {
            const row = payload.new as SessionRecordingRow;
            const recording = rowToRecording(row);
            set((s) => {
              if (s.recordings.some((r) => r.id === recording.id)) return s;
              return { recordings: [recording, ...s.recordings] };
            });
          },
        )
        .subscribe();

      set({ _channel: channel });
    },

    unsubscribe() {
      const supabase = getSafeSupabaseClient();
      const ch = get()._channel;
      if (supabase && ch) void supabase.removeChannel(ch);
      set({ _channel: null });
    },

    reset() {
      get().unsubscribe();
      set({
        recordings: [],
        activeRecordingId: null,
        segments: [],
        isLoading: false,
        error: null,
      });
    },
  }),
);
